import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import Modal, { ConfirmModal } from '../common/modal'
import Pagination from '../common/pagination'
import { deleteItem, getItemList, saveItem } from '../../redux/actions/itemAction'
import { formatDay, formatMoney, getErrorMessage, unitPrice } from '../../utils'

const EMPTY_ITEM = { name: '', companyName: '', address: '', invoiceDate: '', mobile: '', category: 'General', quantity: 0, price: 0, description: '', status: 'Active' }
const LIMIT = 10

const ItemManager = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { itemList, loader } = useSelector(state => state.item)

    const [search, setSearch] = useState('')
    const [status, setStatus] = useState('')
    const [page, setPage] = useState(1)

    const [form, setForm] = useState(null)       // item being added/edited, null when closed
    const [errors, setErrors] = useState({})
    const [saving, setSaving] = useState(false)
    const [toDelete, setToDelete] = useState(null)
    const [deleting, setDeleting] = useState(false)

    const load = useCallback(() => {
        dispatch(getItemList({ page, limit: LIMIT, search: search || undefined, status: status || undefined }))
            .catch(err => toast.error(getErrorMessage(err)))
    }, [dispatch, page, search, status])

    // Debounce typing in the search box
    useEffect(() => {
        const t = setTimeout(load, 300)
        return () => clearTimeout(t)
    }, [load])

    const handleChange = (e) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
        setErrors(prev => ({ ...prev, [name]: '' }))
    }

    const validate = () => {
        const errs = {}
        if (!form.name.trim()) errs.name = 'Item name is required'
        if (form.quantity === '' || Number(form.quantity) < 0) errs.quantity = 'Enter a valid quantity'
        if (form.price === '' || Number(form.price) < 0) errs.price = 'Enter a valid price'
        setErrors(errs)
        return !Object.keys(errs).length
    }

    const handleSave = async (e) => {
        e.preventDefault()
        if (!validate()) return
        setSaving(true)
        try {
            const res = await dispatch(saveItem({ ...form, quantity: Number(form.quantity), price: Number(form.price) }))
            toast.success(res.message)
            setForm(null)
            load()
        } catch (err) {
            toast.error(getErrorMessage(err))
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        setDeleting(true)
        try {
            const res = await dispatch(deleteItem(toDelete._id))
            toast.success(res.message)
            setToDelete(null)
            // Step back a page if we just removed the last row on this one
            if (itemList.result?.length === 1 && page > 1) setPage(page - 1)
            else load()
        } catch (err) {
            toast.error(getErrorMessage(err))
        } finally {
            setDeleting(false)
        }
    }

    const openForm = (item) => {
        setErrors({})
        setForm(item
            ? { ...EMPTY_ITEM, ...item, companyName: item.companyName || '', address: item.address || '', mobile: item.mobile || '', invoiceDate: item.invoiceDate ? item.invoiceDate.slice(0, 10) : '' }
            : { ...EMPTY_ITEM })
    }

    const rows = itemList.result || []

    return (
        <div className="Small-Wrapper">
            <div className="TitleBox">
                <h2>Item Manager</h2>
                <button className="Button" onClick={() => openForm(null)}><i className="fa fa-plus"></i> Add Item</button>
            </div>

            <div className="Filter">
                <div className="form-group">
                    <label>Search</label>
                    <input type="search" className="form-control" placeholder="Name, company, mobile, address or category"
                        value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} />
                </div>
                <div className="form-group">
                    <label>Status</label>
                    <select className="form-control" value={status} onChange={e => { setStatus(e.target.value); setPage(1) }}>
                        <option value="">All</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                    </select>
                </div>
                <div className="form-group">
                    <label>&nbsp;</label>
                    <button className="Button Cancel" title="Reset filters" onClick={() => { setSearch(''); setStatus(''); setPage(1) }}>
                        <i className={`fa fa-refresh ${loader ? 'fa-spin' : ''}`}></i>
                    </button>
                </div>
            </div>

            <div className="TableList">
                <table>
                    <thead>
                        <tr>
                            <th>S.No.</th>
                            <th>Item Name</th>
                            <th>Company Name</th>
                            <th>Mobile</th>
                            <th>Invoice Date</th>
                            <th>Address</th>
                            <th>Category</th>
                            <th>Quantity</th>
                            <th>Price</th>
                            <th>Price / Qty</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((item, i) => (
                            <tr key={item._id}>
                                <td>{(page - 1) * LIMIT + i + 1}</td>
                                <td>{item.name}</td>
                                <td>{item.companyName || '-'}</td>
                                <td className="text-nowrap">{item.mobile || '-'}</td>
                                <td className="text-nowrap">{formatDay(item.invoiceDate)}</td>
                                <td className="AddressCell" title={item.address}>{item.address || '-'}</td>
                                <td>{item.category}</td>
                                <td>{item.quantity}</td>
                                <td>{formatMoney(item.price)}</td>
                                <td>{unitPrice(item) == null ? '-' : formatMoney(unitPrice(item))}</td>
                                <td>
                                    <div className="Actions">
                                        <button className="IconBtn Purple" title="Price Analysis" onClick={() => navigate(`/price-analysis?name=${encodeURIComponent(item.name)}`)}><i className="fa fa-line-chart"></i></button>
                                        <button className="IconBtn Blue" title="Edit" onClick={() => openForm(item)}><i className="fa fa-pencil"></i></button>
                                        <button className="IconBtn Red" title="Delete" onClick={() => setToDelete(item)}><i className="fa fa-trash"></i></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!rows.length && (
                            <tr><td colSpan="11">{loader ? 'Loading...' : 'No items found. Click "Add Item" to create one.'}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="ListFooter">
                <span>Total: {itemList.total || 0}</span>
                <Pagination page={page} pages={itemList.pages || 1} onChange={setPage} />
            </div>

            <Modal show={!!form} onClose={() => !saving && setForm(null)} size="modal-lg">
                {form && (
                    <form className="Category" onSubmit={handleSave} noValidate>
                        <h3>{form._id ? 'Edit Item' : 'Add Item'}</h3>
                        <div className="row">
                            <div className="col-sm-6 form-group">
                                <label>Item Name <sup>*</sup></label>
                                <input className="form-control" name="name" value={form.name} onChange={handleChange} autoFocus />
                                {errors.name && <small className="text-danger">{errors.name}</small>}
                            </div>
                            <div className="col-sm-6 form-group">
                                <label>Company / Vendor Name</label>
                                <input className="form-control" name="companyName" value={form.companyName} onChange={handleChange} placeholder="e.g. ABC Traders" />
                            </div>
                            <div className="col-sm-6 form-group">
                                <label>Mobile Number</label>
                                <input type="tel" className="form-control" name="mobile" value={form.mobile} onChange={handleChange} />
                            </div>
                            <div className="col-sm-6 form-group">
                                <label>Invoice Date</label>
                                <input type="date" className="form-control" name="invoiceDate" value={form.invoiceDate} onChange={handleChange} />
                            </div>
                            <div className="col-12 form-group">
                                <label>Address</label>
                                <textarea rows="2" className="form-control" name="address" value={form.address} onChange={handleChange} />
                            </div>
                            <div className="col-sm-6 form-group">
                                <label>Category</label>
                                <input className="form-control" name="category" value={form.category} onChange={handleChange} />
                            </div>
                            <div className="col-sm-6 form-group">
                                <label>Status</label>
                                <select className="form-control" name="status" value={form.status} onChange={handleChange}>
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                            </div>
                            <div className="col-sm-6 form-group">
                                <label>Quantity</label>
                                <input type="number" min="0" className="form-control" name="quantity" value={form.quantity} onChange={handleChange} />
                                {errors.quantity && <small className="text-danger">{errors.quantity}</small>}
                            </div>
                            <div className="col-sm-6 form-group">
                                <label>Price</label>
                                <input type="number" min="0" step="0.01" className="form-control" name="price" value={form.price} onChange={handleChange} />
                                {errors.price && <small className="text-danger">{errors.price}</small>}
                            </div>
                            <div className="col-12 form-group">
                                <label>Description</label>
                                <textarea rows="3" className="form-control" name="description" value={form.description} onChange={handleChange} />
                            </div>
                        </div>
                        <div className="text-right">
                            <button type="button" className="Button Cancel mr-2" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
                            <button type="submit" className="Button" disabled={saving}>{saving ? 'Saving...' : 'Save Item'}</button>
                        </div>
                    </form>
                )}
            </Modal>

            <ConfirmModal
                show={!!toDelete}
                title="Delete Item"
                message={`Are you sure you want to delete "${toDelete?.name}"? Linked documents will be kept but unlinked.`}
                busy={deleting}
                onCancel={() => setToDelete(null)}
                onConfirm={handleDelete}
            />
        </div>
    )
}

export default ItemManager
